import type { Patron } from "../../types";

export const patrons: Patron[] = [
  {
    id: "benefactor",
    name: "The Benefactor",
    description:
      "A spirit that pities humanity and wishes to convert all living beings into spirits like itself. It is generous, almost to a fault.",
    full_description: [
      {
        type: "paragraph",
        text: "This spirit looks upon humanity with pity, as our lives flare out so quickly and our concerns are so petty. The Benefactor would love nothing more than to convert all of humanity into eternal, formless spirits like itself, instead of leading dirty, painful, pointless lives crawling and squirming upon the surface of our miserable, temporary planet.",
      },
      {
        type: "paragraph",
        text: "Followers of the Benefactor are either kind-hearted volunteers, or mendacious elites who seek to manipulate their public image.",
      },
    ],
    challenges: [
      "Spend more than your starting coin in a single day",
      "Give a valuable gift to someone who needs it",
    ],
  },
  {
    id: "chirurgeon",
    name: "The Chirurgeon",
    description:
      "Obsessed with gaining a physical body, it collects flesh and biological material. It is clinical, curious, and disturbingly interested in anatomy.",
    full_description: [
      {
        type: "paragraph",
        text: "An entity that is obsessed with one day casting off its ethereal form and gaining a physical body, it collects as much flesh from its Warlocks as it can, building a grotesque and bloody palace of living, patchwork tissue for it to study and reside within.",
      },
      {
        type: "paragraph",
        text: "Followers of the Chirurgeon are often excellent healers... or skilled assassins.",
      },
    ],
    challenges: [
      "Heal or stabilise 5 different people in a day",
      "Collect a fleshy souvenir from a victim you landed the killing blow on",
    ],
  },
  {
    id: "fool",
    name: "The Fool",
    description:
      "Regards mortals as stupid and makes a mockery of them at every opportunity. It is witty, cruel, and delights in embarrassment.",
    full_description: [
      {
        type: "paragraph",
        text: "This being appears in a different form every time, as everything from a wise man in rags to a jester with a golden crown. It regards mortals as stupid, pathetic creatures, and delights in making mockery of their dreams and desires. For this creature is not the true fool - it simply holds up a mirror to Warlocks reckless enough to deal with it.",
      },
      {
        type: "paragraph",
        text: "Followers of the Fool often drink a lot, and love the performing arts.",
      },
    ],
    challenges: [
      "Earn 1 gold coin in a day through your own efforts",
      "Make 5 different people laugh",
    ],
  },
  {
    id: "gambler",
    name: "The Gambler",
    description:
      "Calculates the odds of everything and finds the universe to be one great game of chance. It speaks in probabilities and wagers.",
    full_description: [
      {
        type: "paragraph",
        text: "Outwardly, this being presents a friendly smile with too many teeth and an easy demeanor - but inwardly, webs of probability and nebulous calculations tick away in what passes for a mind, as it calculates the odds of every event that occurs in the universe at once.",
      },
      {
        type: "paragraph",
        text: "Followers of the Gambler are mostly very, very poor - but a tiny percentage are fabulously wealthy. If you choose to deal with the Gambler, I’m sure it’ll work out for YOU...",
      },
    ],
    challenges: [
      "Win a game or a bet",
      "Cheat at something and get away with it",
    ],
  },
  {
    id: "judge",
    name: "The Judge",
    description:
      "The ultimate arbiter of good and evil, right and wrong. It alone knows the indisputable truth of the nature of justice itself.",
    full_description: [
      {
        type: "paragraph",
        text: "The ultimate arbiter of good and evil, right and wrong, the Judge alone knows the ultimate, indisputable truth of the nature of justice itself. Mortal philosophers often seek the Judge's council at great risk to themselves to understand the nature of the universe, or settle decades-long debates about ancient truths.",
      },
      {
        type: "paragraph",
        text: "Followers of the Judge have strong moral codes, either leading them to careers in law enforcement, or pushing them to the edges of society as freedom fighters.",
      },
    ],
    challenges: [
      "Deliver justice to someone who has been wronged",
      "Change someone's mind about something they believe",
    ],
  },
  {
    id: "librarian",
    name: "The Librarian",
    description:
      "Keeper of an infinite library, it collects stories, secrets, and experiences. It is quiet, patient, and hungers for new knowledge.",
    full_description: [
      {
        type: "paragraph",
        text: "A cloaked entity that has transformed its domain into an infinite library, where every possible book, scroll, tablet, and map resides. Rumour has it that the deepest of the Librarian's archives are a prison for the most interesting and skilled storytellers and academics of the mortal world, where they may only be allowed to embrace death once every iota of experience and knowledge they possess has been inscribed for posterity.",
      },
      {
        type: "paragraph",
        text: "Followers of the Librarian are often researchers and artists.",
      },
    ],
    challenges: [
      "Discover a secret that someone wanted kept hidden",
      "Write or draw a page of original material",
    ],
  },
  {
    id: "magician",
    name: "The Magician",
    description:
      "Disdains the mortal realms and passes on magic in hopes of eventually disassembling the material world entirely. Cold, precise, and powerful.",
    full_description: [
      {
        type: "paragraph",
        text: "This being has a deep disdain for the mortal realms, seeing them as far beneath its perfect domain of pure thought and energy. It willingly passes on magical power to its Warlocks, hoping to one day disassemble the material world into ethereal whispers of magic.",
      },
      {
        type: "paragraph",
        text: "Followers of the Magician often use overpowered magic to solve simple, everyday problems.",
      },
    ],
    challenges: [
      "Cast 5 different spells in a single day",
      "Use up all of your MP in a single battle",
    ],
  },
  {
    id: "merchant",
    name: "The Merchant",
    description:
      "A mysterious creature that delights in the flow of gold and the art of the deal. It judges all things by their market value.",
    full_description: [
      {
        type: "paragraph",
        text: "Nobody knows what this mysterious creature gains from gold - but it delights in the flow of this shiny metal, and the intricate webs wealth weaves between mortals. Economics is, after all, built on the belief of humans, not dissimilar to magic itself?",
      },
      {
        type: "paragraph",
        text: "Followers of the Merchant are often traders and bankers.",
      },
    ],
    challenges: [
      "Sell something for more than you bought it for",
      "Buy something for less than it's worth",
    ],
  },
  {
    id: "prophets",
    name: "The Prophets",
    description:
      "Three entities — or perhaps one — that speaks in riddles and paradoxes. Their predictions are always true, if you can interpret them correctly.",
    full_description: [
      {
        type: "paragraph",
        text: 'Nobody knows for sure if the Prophets are three separate entities or one. They speak in dense riddles, and frequently use esoteric terms like "quantum possibility".',
      },
      {
        type: "paragraph",
        text: "Followers of the Prophets can be ambitious fortuneseekers, or superstitious oracles.",
      },
      {
        type: "paragraph",
        text: "The Prophet's Challenges do not follow the usual rules for Patron Challenges.",
      },
      {
        type: "paragraph",
        text: "You may earn rewards from completeing this Challenge up to 3 times each day.",
      },
      {
        type: "paragraph",
        text: "Make an unlikely prediction about another character that will occur the next day. You may make one prediction per character, but do it for as many characters as you like. Write your predictions down onto a piece of paper, and submit it to the prophecy box that the GMs have provided before 9pm.",
      },
      {
        type: "paragraph",
        text: "Overnight, the Prophets will decide which prophecies you have made that are worthy of their attention. Bear in mind the Prophets can be fickle and tempestuous - sometimes they will reject prophecies for seemingly no reason, or accept ones that appear impossible! You will find your accepted prophecies ready for collection at breakfast the next day from the prophecy box.",
      },
      {
        type: "paragraph",
        text: "If a prophecy comes true, your Patron Challenge is complete. If a character learns of a prophecy about themselves, the prophecy automatically fails.",
      },
      {
        type: "paragraph",
        text: "Example prophecies may include a noble running out of money, a mighty warrior losing a duel, or a filthy hermit finding love.",
      },
    ],
    challenges: [
      "Predict an event that later comes true (up to 3 per day; must be verified by a GM)",
    ],
  },
  {
    id: "sycophant",
    name: "The Sycophant",
    description:
      "The loneliest entity in existence, it craves interaction and validation above all else. It is desperate, warm, and terribly needy.",
    full_description: [
      {
        type: "paragraph",
        text: "The loneliest entity in the universe, the Sycophant craves interaction and affirmation, valuing the smallest compliment above all other experiences. Warlocks who seek this patron out may find it difficult to escape its domain, as the Sycophant is dangerously obsessive and wishes for company for all eternity.",
      },
      {
        type: "paragraph",
        text: "Followers of the Sycophant are often servants or peasants.",
      },
    ],
    challenges: [
      "Make a new friend",
      "Receive a compliment without asking for one",
    ],
  },
  {
    id: "undying",
    name: "The Undying",
    description:
      "Obsessed with death, the Undying wishes to wipe all life from existence. It is ancient, patient, and utterly without mercy.",
    full_description: [
      {
        type: "paragraph",
        text: "What does not live can never die, and this unsettling creature has formed a laser-focussed obsession on what it cannot have. The Undying would wipe all life from the universe if it could, extinguishing itself last just to watch, before relishing the impossible experience itself.",
      },
      {
        type: "paragraph",
        text: "Followers of the Undying are thrill seekers, and have a reputation for being a liability as much as an asset.",
      },
    ],
    challenges: [
      "Lose 8 HP in a single day without bleeding out",
      "Participate in a combat from start to finish without taking any damage",
    ],
  },
  {
    id: "warrior",
    name: "The Warrior",
    description:
      "Revels in pain and death and cannot differentiate humans from beasts. It respects only strength and violence.",
    full_description: [
      {
        type: "paragraph",
        text: "This Patron revels in the mortal experience of pain and death, but also the honour of strength and combat. It has difficulty differentiating between human beings and the wild beasts they claim to be above.",
      },
      {
        type: "paragraph",
        text: "Followers of the Warrior often pledge themselves to wealthy houses as soldiers of fortune, seeking great battles and glory.",
      },
    ],
    challenges: [
      "Drop a worthy adversary to 0 HP",
      "Drop to 0 HP against a worthy adversary",
    ],
  },
  {
    id: "witness",
    name: "The Witness",
    description:
      "Wishes to record every instant of creation, from the dawn of time to time's end. If an action is not witnessed, did it ever occur at all?",
    full_description: [
      {
        type: "paragraph",
        text: "The Witness wishes to record every instant of creation, from the tiniest movements of subatomic particles at the dawn of time, to the death of stars at time's end. For is it not true that if an action is not witnessed, then it may as well have never occurred at all?",
      },
      {
        type: "paragraph",
        text: "Scientists of all kinds follow the teachings of the Witness, as they seek to observe and understand every aspect of the physical universe.",
      },
    ],
    challenges: [
      "Observe something that has never been observed before",
      "Teach someone something they did not know",
    ],
  },
  // Previous Patrons: no longer part of the Thirteen
  {
    id: "tinker",
    name: "The Tinker",
    description:
      "A paradoxical being that constantly reinvents itself. It is endlessly curious about how things work and how they might be improved.",
    full_description: [
      {
        type: "paragraph",
        text: "A paradoxical being who is constantly reinventing themselves and the realm they reside in. This creature reshapes reality itself in an effort to understand the essence of the universe. Some whisper it created our universe, and is attempting to design a newer, improved version...",
      },
      {
        type: "paragraph",
        text: "Followers of the Tinker are often inventors and alchemists.",
      },
    ],
    challenges: [
      "Fix something that is broken, or recharge a magic item",
      "Create something valuable",
    ],
    exiled: true,
  },
  {
    id: "trickster",
    name: "The Trickster",
    description:
      "Spins fact and fiction into an indistinguishable web. It is playful, deceptive, and delights in confusion.",
    full_description: [
      {
        type: "paragraph",
        text: "A garrulous being that spins fact and fiction into indecipherable knots, and delights in making mortals doubt the truths they take for granted. This entity has the power to twist the fabric of spacetime itself to retroactively transform lies into reality.",
      },
      {
        type: "paragraph",
        text: "Followers of the Trickster are commonly comedians or politicians.",
      },
    ],
    challenges: [
      "Have someone believe a lie",
      "Find the solution to a difficult puzzle or riddle",
    ],
    exiled: true,
  },
];
